

















































































































































































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import PhoneEditingHintComponent from "@/components/admin/PhoneEditingHint.vue";
import { applyCompactGraphOptions, graphSizeProfileFor } from "@/utils/DryingGraphCompactOptions";
import { setUpdateHold } from "@/utils/UpdateHold";
import router from "@/router/router";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";
import { adminPortalChangeAccess, viewPowerInformationAccess } from "@/permissions/UserPermissions";
import { ClientModule } from "@/store/modules/ClientModule";
import { ComponentName } from "@/utils/RouteGuards";
import DryingProgressGraphComponent from "@/components/panel/DryingProgressGraph.vue";
import DryingProgressGraphLegendComponent from "@/components/panel/DryingGraphLegend.vue";
import RenoTimeSeriesComponent from "@/components/devices/quality/RenoTimeSeries.vue";
import RenoReviewRatingComponent from "@/components/admin/renoReview/RenoReviewRating.vue";
import RenoReviewNoteRowComponent from "@/components/admin/renoReview/RenoReviewNoteRow.vue";
import {
  LegendDisplayMode,
  TargetSensors,
} from "@/components/common/SelectionTable.vue";
import {
  getIresDeviceCategory,
  IresDeviceCategory,
} from "@/data/models/IresDevice";
import { ComBox, getComBoxProgramName } from "@/data/models/ComBox";
import {
  CURRENT_RENO_MODEL_VERSION,
  renoReviewerKey,
  RenoReview,
  RenoReviewDeviceType,
  RenoReviewFilters,
  RenoReviewNote,
  RenoReviewQueueEntry,
  RenoReviewSuppression,
  emptyReviewFilters,
  parseFilterDate,
} from "@/data/models/RenoReviewEntry";
import {
  loadChainsForDevice,
  loadDeviceHistory,
  loadNote,
  loadReview,
  loadReviewDevices,
  loadSuppression,
  removeSuppression,
  saveNote,
  saveReview,
  saveSeen,
  saveSuppression,
} from "@/data/repository/RenoReviewRepository";
import { getActiveUserId } from "@/data/repository/UserRepository";
import { getClientId } from "@/data/repository/ClientRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  currentDevice,
  currentEntry,
  emptyCursor,
  landOnChain,
  localPosition,
  next,
  previous,
  RenoReviewCursor,
  RenoReviewStep,
  RenoReviewStepOutcome,
  settleOnDevice,
  startCursor,
} from "@/utils/RenoReviewCursor";
import { getIresDevice } from "@/data/repository/IresDeviceRepository";
import { getProgramChain } from "@/data/repository/ProgramChainRepository";
import { IresDevice } from "@/data/models/IresDevice";
import {
  doesProgramChainIncludeActiveRun,
  getEndDateOfProgramChain,
  ProgramChain,
} from "@/data/models/ProgramChain";
import {
  DryingProgressChartData,
  extractDryingProgressGraphData,
} from "@/utils/DryingProgressGraphDataExtractor";
import { WeatherInformationDetailsLevel } from "@/utils/DryingGraphYAxis";
import * as graphPanel from "@/utils/DryingGraphPanelState";
import {
  formatDateTohDateMonthYearString,
  formatDateToHoursAndMinuteString,
} from "@/utils/DateHelper";
import {
  COLOR_TABLE_HEADER,
  COLOR_TABLE_HEADER_DARK,
  COLOR_WHITE_BACKGROUND,
  COLOR_WHITE_BACKGROUND_DARK,
} from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { getClient, getClientName } from "@/data/repository/ClientRepository";
import ToolbarOverflowItemComponent from "@/components/common/ToolbarOverflowItem.vue";

/** How many devices to pull per page of the device track. */
const DEVICE_PAGE_SIZE = 200;

/**
 * The reviewer actions that persist something, so the bar can show which one is in flight.
 *
 * One at a time, always: two overlapping steps skip a drying, and two overlapping writes to the same
 * note document race each other. The last two do **not** move — they toggle a mark and stay put.
 */
type RenoReviewAction =
  | "previous"
  | "next"
  | "rate"
  | "toggleChainSuspect"
  | "toggleSuppress";

/** Everything a drying needs to render, so a prefetch and a load can hand over the same thing. */
interface DryingData {
  device: IresDevice;
  chain: ProgramChain;
  review: RenoReview | null;
  suppression: RenoReviewSuppression | null;
  note: RenoReviewNote | null;
}

/** Breathing room between the scrollable graph block and the footer. */
const GRAPH_VIEWPORT_MARGIN = 16;

/** Floor for the graph block, so a mid-transition measurement cannot collapse it. */
const MIN_GRAPH_VIEWPORT_HEIGHT = 320;

/** The overlay legend's gap from the plot's top edge and from the right axis, as on the device detail. */
const LEGEND_OVERLAY_INNER_MARGIN = 8;

/**
 * Review stepper: walk drying to drying and look at the RENO result.
 *
 * Traversal is device-major, chain-newest-first (D19); all of that logic lives in
 * `RenoReviewCursor` and is unit-tested, so this component only performs the I/O the cursor asks
 * for and renders the result.
 *
 * Two kinds of control, deliberately: the ratings and Zurück / Weiter **move**, while the chain report
 * and the hide are **toggles** that persist on press and leave the reviewer on the drying. That split
 * is what lets one drying carry a verdict, a chain report and a hide at the same time.
 */
@Component({
  components: {
    "toolbar-overflow-item": ToolbarOverflowItemComponent,
    "drying-progress": DryingProgressGraphComponent,
    "drying-progress-legend": DryingProgressGraphLegendComponent,
    "reno-time-series": RenoTimeSeriesComponent,
    "reno-review-rating": RenoReviewRatingComponent,
    "reno-review-note-row": RenoReviewNoteRowComponent,
    "phone-editing-hint": PhoneEditingHintComponent,
  },
})
export default class RenoReviewStepperComponent extends Vue {
  cursor: RenoReviewCursor = emptyCursor();

  iresDevice: IresDevice | null = null;
  programChain: ProgramChain | null = null;

  /**
   * Sensor selection driving the graph, owned by the legend.
   *
   * `chainId` is the interlock: the graph stays hidden until the legend has reported a selection
   * for the chain currently on screen, so stepping never draws one drying's sensors over another's.
   */
  targetSensors: TargetSensors = {
    showPhases: true,
    chainId: "",
    selectedCombinations: [],
  };

  /**
   * Resolved client names, keyed by client id.
   *
   * A plain reactive object rather than a Map because Vue 2 cannot track Map mutations. Cached for
   * the life of the screen: a device's dryings all belong to few clients, so re-reading the client
   * document on every step would be pure waste.
   */
  clientNamesById: Record<string, string> = {};

  extractedGraphData: DryingProgressChartData | null = null;
  legendDisplayMode: LegendDisplayMode = LegendDisplayMode.OVERLAY;

  /**
   * Whether the legend's power toggle is on, kept across dryings like the legend's own state.
   *
   * The consumption line is built from the runs already loaded for the graph, so this costs no read and
   * is available on every drying. Weather is not wired here, and is a decision rather than an oversight:
   * see the note on {@link computeDryingGraphData}.
   */
  showPowerLine = false;
  legendHighlightColors: string[] | null = null;

  /** Measured cap for the scrollable graph block; 0 means "no cap". */
  graphViewportMaxHeight = 0;

  @Ref("graphViewport") graphViewport?: HTMLElement;

  /** Kept so the listener can be removed again; an anonymous handler could not be. */
  private readonly onWindowResize = () => this.updateGraphViewportHeight();

  graphChartArea: { left: number; right: number; width: number; top: number } = {
    left: 0,
    right: 0,
    width: 0,
    top: 0,
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  graphVisibleRange: { xMin: number; xMax: number; startDate: Date; timeScale: any } | null = null;

  /** The stored verdict for the drying on screen. Pressing a rating writes and steps on. */
  rating: number | null = null;
  savedReview: RenoReview | null = null;

  /**
   * Whether this drying is on the reviewer's hide list.
   *
   * A boolean rather than the suppression document: nothing here reads its fields, and it is written
   * and deleted by a toggle, so what matters is only whether the document exists right now.
   */
  isSuppressed = false;
  isSavingRating = false;

  /** Which rating button was pressed, so only that one spins. */
  pendingRating: number | null = null;

  /** The reviewer's free-text comment on this drying, flushed on any navigation. */
  comment = "";
  /** Whether this drying is reported as having a broken program chain. */
  chainSuspect = false;
  savedNote: RenoReviewNote | null = null;

  isLoading = true;
  /** Whether the review was started; on a phone it waits until the screen is wide enough. */
  hasStarted = false;
  loadError = false;

  /**
   * The action currently running, or null when idle. Drives every control in the bar.
   *
   * Set **synchronously** by {@link runAction} before the first await, which is the whole point: the
   * old flags were raised deep inside the work (`isSavingRating` only when there was something to
   * write, `isLoading` only once the reads for the *next* drying began), so between the click and the
   * first of them the buttons stayed enabled with no spinner anywhere. A second press in that window
   * ran a second step, and the reviewer skipped a drying without knowing. A `:disabled` binding alone
   * cannot close it either: Vue applies it on the next tick, and a double click lands inside one.
   */
  pendingAction: RenoReviewAction | null = null;

  /**
   * The one drying read kept in hand: see {@link readDrying}.
   *
   * Declared without an initializer on purpose, so it stays off Vue's reactive data: nothing renders it,
   * and there is no point observing a promise.
   */
  private dryingRead?: { key: string; data: Promise<DryingData> };

  /** True when the filters matched no device at all, distinct from having reached the end. */
  isEmptyQueue = false;

  /**
   * The next drying, resolved ahead of time.
   *
   * Two jobs at once: it is what disables Weiter on the last prediction, and it is the step actually
   * applied when the reviewer moves on, so the chains it loaded are not loaded twice. Null while the
   * lookahead is still running or after it was invalidated by a move.
   */
  private nextStep: RenoReviewStep | null = null;

  /**
   * The previous drying, resolved ahead of time. The mirror of {@link nextStep}.
   *
   * Needed for the same reason: "is there an earlier drying" cannot be answered from the cursor, because
   * a device whose chains all filter out is skipped, so finding out takes the walk itself. Free when the
   * current device has an earlier chain, and one read per skipped device when crossing a boundary.
   */
  private previousStep: RenoReviewStep | null = null;

  /** Discards a slow load whose drying is no longer the one on screen. */
  private loadToken = 0;

  get modelVersion(): string {
    return CURRENT_RENO_MODEL_VERSION;
  }

  /** The shared IRES admin login sees the admin portal read-only (#2610071): it may look, not rate. */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  /**
   * Path segment identifying this reviewer's stores.
   *
   * Client **and** user, because a user id is only unique within its client. The security rule
   * rebuilds the same string from the token, so a mismatch here is a permission denial, not a
   * silently misfiled document.
   */
  get reviewerKey(): string {
    return renoReviewerKey(getClientId(), getActiveUserId());
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /**
   * Footer background.
   *
   * The page surface, not the table-header grey: the bar is where you work, not a form footer, and
   * the grey made it read as a separate panel bolted to the bottom. Opaque rather than transparent,
   * because content scrolls underneath it.
   */
  get footerColor() {
    return themed(COLOR_WHITE_BACKGROUND, COLOR_WHITE_BACKGROUND_DARK);
  }

  get isShowingComBoxDetails(): boolean {
    return (
      !!this.iresDevice &&
      getIresDeviceCategory(this.iresDevice) === IresDeviceCategory.COMBOX
    );
  }

  /**
   * Graph payload, or null while the legend's selection still belongs to the previous drying.
   *
   * Same interlock the device page applies, and the reason the graph was blank before: without a
   * selection `extractDryingProgressGraphData` produces no sensor series at all.
   */
  get dryingProgressChartData(): DryingProgressChartData | null {
    if (!this.programChain || this.programChain.id !== this.targetSensors.chainId) {
      return null;
    }

    return this.extractedGraphData;
  }

  get isLegendBelow(): boolean {
    return this.legendDisplayMode === LegendDisplayMode.BELOW;
  }

  /**
   * Caps the graph block's height so it scrolls instead of pushing the footer off the screen.
   *
   * Only applied in legend-below mode; in overlay mode the block already fits. Measured rather than
   * hardcoded as a `calc()`: the space available depends on the sticky header's rendered height and
   * on the footer's, neither of which is a constant.
   */
  get graphViewportStyle(): Record<string, string> {
    if (!this.isLegendBelow || this.graphViewportMaxHeight <= 0) {
      return {};
    }

    return { "max-height": `${this.graphViewportMaxHeight}px` };
  }

  /**
   * Recomputes the cap from where the block actually starts to where the footer begins.
   *
   * Subtracts the same three layout slots `v-content` pads its bottom with, so the space the footer
   * occupies is not reserved twice. All three rather than one: `v-footer app` registers as `footer`,
   * but `v-footer app inset` registers as `insetFooter`, and `bottom` belongs to a bottom
   * navigation. Summing them stays correct whichever is in play.
   */
  private updateGraphViewportHeight() {
    const element = this.graphViewport;

    if (!element || !this.isLegendBelow) {
      this.graphViewportMaxHeight = 0;
      return;
    }

    const application = this.$vuetify.application;
    const reservedAtBottom =
      application.footer + application.insetFooter + application.bottom;

    const available =
      window.innerHeight -
      element.getBoundingClientRect().top -
      reservedAtBottom -
      GRAPH_VIEWPORT_MARGIN;

    // Never collapse to something unusable if the measurement comes out small mid-transition.
    this.graphViewportMaxHeight = Math.max(available, MIN_GRAPH_VIEWPORT_HEIGHT);
  }

  /**
   * The overlay legend inside the plot, as on the device detail: below the plot's top edge and left of
   * the right axis. At its fixed 20 px from the top and flush right it covered the right axis's title,
   * whose "(%)" line was hidden at every width (2026-11-03). Until the graph has laid out, the CSS
   * position stands.
   */
  get legendStyle(): Record<string, string> {
    const area = this.graphChartArea;
    if (this.isLegendBelow || area.right <= 0 || area.width <= 0) {
      return {};
    }
    return {
      top: `${area.top + LEGEND_OVERLAY_INNER_MARGIN}px`,
      right: `${area.width - area.right + LEGEND_OVERLAY_INNER_MARGIN}px`,
    };
  }

  get dryingGraphLegendClass(): string {
    return this.isLegendBelow ? "drying-graph-legend-below mt-2" : "drying-graph-legend";
  }

  onTargetSensorsChanged(targetSensors: TargetSensors) {
    this.targetSensors = targetSensors;
    this.computeDryingGraphData();
  }

  onComBoxHumidityChanged() {
    this.computeDryingGraphData();
  }

  onShowPowerInformationChanged(showPowerLine: boolean) {
    this.showPowerLine = showPowerLine;
    this.computeDryingGraphData();
  }

  onLegendDisplayModeChanged(mode: LegendDisplayMode) {
    this.legendDisplayMode = mode;

    // After the DOM reflows to the new mode, so the measurement sees the real layout.
    this.$nextTick(() => this.updateGraphViewportHeight());
  }

  onLegendHover(colors: string[] | null) {
    this.legendHighlightColors = colors;
  }

  /** Zoom is how a multi-hundred-day drying stays readable; the graph owns the interaction. */
  onZoomStateChanged() {
    // Nothing to track here: unlike the device page there is no reset-zoom button to enable.
  }

  /** The "start at device" value in force, for the empty-state hint. */
  get startAtSerial(): string {
    return (this.$route.query.startAtSerial as string) || "";
  }

  get serialNo(): string {
    return currentEntry(this.cursor)?.serialNo ?? currentDevice(this.cursor)?.serialNo ?? "";
  }

  /**
   * The owning client's display name, or a placeholder until it resolves.
   *
   * Never the raw client id: falling back to it made the header flash a 20-character document id on
   * every step and then swap it for the name, which reads as a glitch and tells the reviewer nothing.
   * The dash is the same placeholder the other header fields use for a value they do not have.
   */
  get clientName(): string {
    const clientId = currentEntry(this.cursor)?.clientId;

    if (!clientId) {
      return "—";
    }

    return this.clientNamesById[clientId] ?? "—";
  }

  /**
   * The drying's own number, the one the device's history and log show, plus its program.
   *
   * Distinct from `positionLabel`, which is a position among the current *device's* dryings. Both
   * matter: one identifies the drying, the other says how far you have got through this device.
   */
  get dryingLabel(): string {
    const chainNumber = currentEntry(this.cursor)?.chainNumber;

    return chainNumber
      ? this.$t("panelDetail.dryingProgressGraphControl.formatDrying", [
          chainNumber,
        ]).toString()
      : "—";
  }

  /**
   * Drying program by name ("Überdruck", "Raumtrocknung", …) rather than as "P2".
   *
   * `getComBoxProgramName` resolves the fixed programs and then the client's own configured ones off
   * the device, and falls back to "P{n}" when it cannot name one, so an unknown program still shows
   * something rather than a blank.
   *
   * The number comes from the loaded chain's first run in preference to the queue entry: Assist
   * leaves `programNumber` null on some index entries, and the run is where the graph reads it from,
   * so this agrees with the P-band drawn on the chart.
   */
  get programLabel(): string {
    const programNumber =
      this.programChain?.runs?.[0]?.programNumber ??
      currentEntry(this.cursor)?.programNumber;

    if (programNumber == null) {
      return "—";
    }

    // RENO is ComBox-only, but guarded so a Panel could never be labelled with ComBox program names.
    if (this.iresDevice && this.isShowingComBoxDetails) {
      return getComBoxProgramName(this, programNumber, this.iresDevice as ComBox);
    }

    // `formatDryingProgram` sits on `panelDetail` itself, NOT under `dryingProgressGraphControl`
    // where `formatDrying` lives. Getting that wrong renders the key verbatim, with no error.
    return this.$t("panelDetail.formatDryingProgram", [programNumber]).toString();
  }

  /**
   * Whether the drying on screen is still running, **from the chain itself** once it has loaded.
   *
   * The queue entry is only a fallback, for the moment before the chain arrives. It is a snapshot Assist
   * takes when it computes a prediction, and Assist re-indexes a chain only when it predicts for it
   * again — which for a finished chain may be never. So an entry freezes at whatever the chain looked
   * like then: 256 of the 684 entries marked running described chains that had already finished, some
   * months earlier.
   *
   * This screen loads the real chain anyway, to draw the graph, so the index has no business deciding
   * what is displayed. It selects which dryings to walk; the chain says what they are.
   */
  get isDryingActive(): boolean {
    if (this.programChain) {
      return doesProgramChainIncludeActiveRun(this.programChain);
    }

    return currentEntry(this.cursor)?.isActive ?? false;
  }

  get chainStartLabel(): string {
    const start = currentEntry(this.cursor)?.chainStart;

    return start ? this.formatMoment(start.toDate()) : "—";
  }

  /** Date and time together; the repo has no combined helper, so the two are composed here. */
  private formatMoment(date: Date): string {
    return `${formatDateTohDateMonthYearString(date)} ${formatDateToHoursAndMinuteString(date)}`;
  }

  /**
   * End of the drying, or a dash when there is none.
   *
   * A running drying simply has no end date, so it reads as the same blank the other header fields
   * use rather than as a status word. That the drying is still running is already visible from the
   * graph, and it is selectable as a filter.
   */
  /**
   * The drying's end, from the chain itself once loaded, and only then from the index.
   *
   * Same reason as {@link isDryingActive}: a stale index entry showed "Ende —" for dryings that had
   * finished months earlier, including 20 of the 34 Hans had rated. A running drying still shows a dash,
   * because it genuinely has no end yet.
   */
  get chainEndLabel(): string {
    if (this.programChain) {
      if (doesProgramChainIncludeActiveRun(this.programChain)) {
        return "—";
      }

      return this.formatMoment(getEndDateOfProgramChain(this.programChain).toDate());
    }

    const chainEnd = currentEntry(this.cursor)?.chainEnd;

    return chainEnd ? this.formatMoment(chainEnd.toDate()) : "—";
  }

  /**
   * The drying's id, from the **queue entry** rather than from the loaded chain.
   *
   * Taken from the chain, it vanished on exactly the screen that needs it: when a load failed, the
   * reviewer saw "Die Trocknung konnte nicht geladen werden" over a RENO row with no id, and so could
   * not say which drying had failed. The entry is what the cursor points at and is always there.
   */
  get currentChainId(): string {
    return currentEntry(this.cursor)?.chainId ?? this.programChain?.id ?? "";
  }

  /**
   * True while an action is running, so no control invites a second press.
   *
   * Deliberately one flag for all of them rather than per-button conditions: the bar was inconsistent,
   * with the rating scale disabled during a write while Zurück and Weiter were not.
   */
  get isBusy(): boolean {
    return this.pendingAction !== null || this.isLoading || this.isSavingRating;
  }

  /**
   * Runs one reviewer action, once.
   *
   * The `isBusy` check is synchronous and therefore the real guard; the `:disabled` bindings are what
   * makes it visible. A rejected press is simply dropped: re-queueing it would step twice, which is
   * the bug this exists to prevent.
   */
  private async runAction(kind: RenoReviewAction, action: () => Promise<void>) {
    if (this.isBusy) {
      return;
    }

    this.pendingAction = kind;

    try {
      await action();
    } finally {
      this.pendingAction = null;
    }
  }

  get positionLabel(): string {
    const position = localPosition(this.cursor);

    return position
      ? this.$t("admin.renoReview.dryingPosition", [position.index, position.total]).toString()
      : "";
  }

  /** Rebuilds the filters the queue screen passed through the route. */
  get filters(): RenoReviewFilters {
    const query = this.$route.query;
    const periodMode = (query.periodMode as string) ?? "none";
    const rawTypes = ((query.deviceTypes as string) ?? "")
      .split(",")
      .filter((value) => value.length > 0);

    return {
      ...emptyReviewFilters(CURRENT_RENO_MODEL_VERSION),
      deviceTypes: rawTypes as RenoReviewDeviceType[],
      maxAgeMonths:
        periodMode === "age" ? Number(query.maxAgeMonths ?? 12) : null,
      fromDate:
        periodMode === "from"
          ? parseFilterDate(query.fromDate as string)
          : null,
      activeOnly: periodMode === "active",
      clientId: ((query.clientId as string) ?? "") || null,
      hideRated: query.hideRated === "true",
      hideSeen: query.hideSeen === "true",
      onlyRatings: ((query.onlyRatings as string) ?? "")
        .split(",")
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0),
      onlyChainSuspect: query.onlyChainSuspect === "true",
      onlyWithComment: query.onlyWithComment === "true",
      onlySuppressed: query.onlySuppressed === "true",
    };
  }

  onChartAreaUpdated(area: { left: number; right: number; width: number; top: number }) {
    this.graphChartArea = area;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onVisibleRangeChanged(range: { xMin: number; xMax: number; startDate: Date; timeScale: any } | null) {
    this.graphVisibleRange = range;
  }

  /**
   * One device's chains, reduced to what this reviewer should actually be shown.
   *
   * Two opposite operations, in order: drop what is excluded (suppressions always, rated and
   * merely-seen on request), then — if the reviewer asked for specific ratings — keep only those.
   *
   * Filtering here rather than inside the cursor keeps the cursor pure, and a device left with no
   * chains is already handled: `settleOnDevice` skips it and carries on the same way, which is
   * exactly the wanted behaviour.
   */
  private async loadVisibleChains(serialNo: string): Promise<RenoReviewQueueEntry[]> {
    const filters = this.filters;

    const [chains, history] = await Promise.all([
      loadChainsForDevice(serialNo, filters),
      loadDeviceHistory(this.reviewerKey, serialNo, filters),
    ]);

    return chains.filter((chain) => {
      if (history.excluded.has(chain.chainId)) {
        return false;
      }

      // Whose drying it was. Filtered here rather than in the query, because pairing it with the
      // chainStart ordering would need a composite index that is not deployed, and one device's chains
      // are few enough that it costs nothing.
      if (filters.clientId && chain.clientId !== filters.clientId) {
        return false;
      }

      if (
        filters.onlyRatings.length > 0 &&
        !filters.onlyRatings.includes(history.ratingByChainId[chain.chainId])
      ) {
        return false;
      }

      if (
        filters.onlyChainSuspect &&
        !history.chainSuspectChainIds.has(chain.chainId)
      ) {
        return false;
      }

      // The one filter that keeps what every other pass drops. `loadDeviceHistory` leaves suppressions
      // out of `excluded` when it is set, so the chains reach here to be selected on.
      if (
        filters.onlySuppressed &&
        !history.suppressedChainIds.has(chain.chainId)
      ) {
        return false;
      }

      return (
        !filters.onlyWithComment ||
        history.commentedChainIds.has(chain.chainId)
      );
    });
  }

  /**
   * Drives a cursor step to completion, performing whatever chain loads it asks for.
   *
   * The cursor may ask repeatedly when devices filter out to nothing, which is how the
   * empty-device skip works.
   */
  private async applyStep(
    step: RenoReviewStep,
    direction?: "previous" | "next"
  ) {
    const settled = await this.settleStep(step);

    if (settled.outcome === RenoReviewStepOutcome.AT_BOUNDARY) {
      /*
      Record what this walk just proved, in the direction it was walking. A press can reach a boundary
      before the background lookahead has answered, and without this the button would stay enabled and
      unexplained until something else resolved it. With it, the lookahead is the single source of truth
      for "is there a neighbour", whether that was learned in the background or by pressing.

      `startCursor` passes no direction: an empty result there means the whole queue is empty, which the
      caller reports as its own state rather than as a boundary.
      */
      if (direction === "next") {
        this.nextStep = settled;
      } else if (direction === "previous") {
        this.previousStep = settled;
      }

      // Nothing changes at a boundary, neither the cursor nor the lookahead.
      //
      // Not the cursor, because a boundary cursor carries no current entry: assigning it emptied the
      // header while the graph stayed up. That was invisible while a boundary could only be reached by
      // pressing Weiter with nothing to save, and is not now that a rating both writes and stays.
      //
      // The cursor is left alone: a boundary cursor carries no current entry, so assigning it emptied
      // the header while the graph stayed up. Invisible while a boundary could only be reached by
      // pressing Weiter with nothing to save; wrong now that a rating both writes and stays.
      return false;
    }

    this.cursor = settled.cursor;
    // Anything resolved ahead or behind described the position just left, in both directions.
    this.nextStep = null;
    this.previousStep = null;

    return true;
  }

  /**
   * Runs a cursor step to completion, performing the chain loads it asks for, without touching state.
   *
   * Separate from {@link applyStep} so the lookahead can resolve the next drying without moving the
   * reviewer: the cursor is immutable, so the same walk serves both.
   */
  private async settleStep(step: RenoReviewStep): Promise<RenoReviewStep> {
    let current = step;

    while (current.outcome === RenoReviewStepOutcome.NEEDS_DEVICE_CHAINS) {
      const device = current.deviceToLoad;

      if (!device) {
        break;
      }

      const chains = await this.loadVisibleChains(device.serialNo);
      current = settleOnDevice(current.cursor, chains, current.landOn ?? "newest");
    }

    return current;
  }

  /**
   * Resolves the drying after this one, in the background.
   *
   * This is what lets Weiter be disabled on the last prediction: "is there a next" cannot be answered
   * from the cursor alone, because a device whose chains all filter out is skipped, so it takes the
   * same walk the step itself would take. Free when the current device has another chain, and bounded
   * by the loaded device page otherwise.
   *
   * The result is kept and applied by {@link stepForward}, so the walk is paid for once rather than
   * again on the actual step.
   */
  private async resolveNextStep() {
    const token = this.loadToken;

    try {
      const settled = await this.settleStep(next(this.cursor));

      // A move or a reload while this ran makes the answer describe the wrong position.
      if (token === this.loadToken) {
        this.nextStep = settled;
        // Now that the drying behind Weiter is known, start reading it.
        this.prefetchNextDrying();
      }
    } catch {
      // Leave it unresolved: Weiter stays enabled and the step itself will report the failure.
    }
  }

  /**
   * Resolves the drying before this one, in the background. The mirror of {@link resolveNextStep}.
   *
   * Same reasoning: the cursor cannot say whether an earlier drying exists, because a device whose
   * chains all filter out is skipped, so it takes the walk. Also cached and reused by
   * {@link onPrevious}, so Zurück is instant rather than paying for the walk twice.
   */
  private async resolvePreviousStep() {
    const token = this.loadToken;

    try {
      const settled = await this.settleStep(previous(this.cursor));

      if (token === this.loadToken) {
        this.previousStep = settled;
      }
    } catch {
      // Leave it unresolved: Zurück stays enabled and the step itself will report the failure.
    }
  }

  /**
   * Whether Weiter has anywhere to go.
   *
   * Enabled while the lookahead is still unresolved, because the reviewer should not have to wait on
   * a background query to be allowed to move, and pressing it merely reports the boundary.
   */
  get canGoNext(): boolean {
    if (this.isSavingRating) {
      return false;
    }

    return (
      this.nextStep === null ||
      this.nextStep.outcome !== RenoReviewStepOutcome.AT_BOUNDARY
    );
  }

  /**
   * The hint beside Zurück, shown as soon as there is nothing behind rather than after a failed press.
   *
   * Derived from the same lookahead that disables the button, so the two cannot disagree: a greyed-out
   * button with no explanation is a dead control the reviewer has to poke to understand.
   */
  get atFirstHint(): string {
    return this.previousStep?.outcome === RenoReviewStepOutcome.AT_BOUNDARY
      ? this.$t("admin.renoReview.atFirst").toString()
      : "";
  }

  /** The mirror beside Weiter. */
  get atLastHint(): string {
    return this.nextStep?.outcome === RenoReviewStepOutcome.AT_BOUNDARY
      ? this.$t("admin.renoReview.atLast").toString()
      : "";
  }

  /**
   * Whether Zurück has anywhere to go. The mirror of {@link canGoNext}, including staying enabled while
   * the lookahead is unresolved: pressing it then reveals the boundary, which is recorded.
   */
  get canGoPrevious(): boolean {
    if (this.isSavingRating) {
      return false;
    }

    return (
      this.previousStep === null ||
      this.previousStep.outcome !== RenoReviewStepOutcome.AT_BOUNDARY
    );
  }

  /** The stored verdict as the scale wants it: one filled step, or none on an unrated drying. */
  get selectedRatings(): number[] {
    return this.rating === null ? [] : [this.rating];
  }

  /** True when the comment or the chain report differ from what is stored for this drying. */
  get hasPendingNote(): boolean {
    return (
      this.comment.trim() !== (this.savedNote?.comment ?? "") ||
      this.chainSuspect !== (this.savedNote?.chainSuspect ?? false)
    );
  }

  /**
   * Whether the reviewer is still on this screen.
   *
   * A step outlives the screen: it keeps running after Filter ändern, a sidebar entry or the browser's
   * Back button, and its tail must not act as if the reviewer were still watching. `$route` is read from
   * the root instance, so it reports the *current* route even from a destroyed component.
   */
  private get isOnStepperRoute(): boolean {
    return this.$route.name === ComponentName.RENO_REVIEW_STEPPER;
  }

  /**
   * Mirrors the cursor into the address bar, so a reload resumes on the drying the reviewer was on
   * instead of restarting the pass at its first device.
   *
   * Three deliberate choices:
   *
   * - `atSerial`/`atChainId`, **not** the filter screen's `startAtSerial`. That one is a filter the
   *   reviewer typed; overwriting it every step would silently rewrite their filter, and it also
   *   truncates the device track, so Zurück could no longer reach the dryings before the reload.
   * - `replace`, not `push`, or the browser's own Back would become a per-drying undo, competing with
   *   Zurück and with the app bar's way out to the filters.
   * - Called from here, the one place reached exactly once per landing, rather than from `applyStep`:
   *   a resumed load adjusts the cursor *after* the step is applied.
   */
  private syncResumeLocation(entry: RenoReviewQueueEntry) {
    if (!this.isOnStepperRoute) {
      return;
    }

    const query = this.$route.query;

    if (query.atSerial === entry.serialNo && query.atChainId === entry.chainId) {
      return;
    }

    router.replace({
      name: ComponentName.RENO_REVIEW_STEPPER,
      query: { ...query, atSerial: entry.serialNo, atChainId: entry.chainId },
    });
  }

  /** Loads the device, chain and graph data for whatever the cursor now points at. */
  private async loadCurrentDrying() {
    const entry = currentEntry(this.cursor);

    if (!entry) {
      this.isLoading = false;
      return;
    }

    const token = ++this.loadToken;
    this.isLoading = true;
    this.loadError = false;

    // Cleared BEFORE the awaits, not after. The cursor already points at the new drying, so a
    // rating left over from the previous one would be written onto this drying by the next Weiter
    // if any of the loads below threw.
    this.rating = null;
    this.savedReview = null;
    this.isSuppressed = false;
    this.savedNote = null;
    this.comment = "";
    this.chainSuspect = false;

    // All three depend on the queue entry alone, not on the drying loading, so they run BEFORE the
    // awaits: with them after, one failed read left the header without its client name and the two
    // navigation buttons without their answers, on the very screen that most needs to say where you are.
    this.resolveClientName(entry.clientId);
    this.resolveNextStep();
    this.resolvePreviousStep();
    this.syncResumeLocation(entry);

    try {
      // Already in hand when the lookahead prefetched it, which is the ordinary case for Weiter.
      const { device, chain, review, suppression, note } = await this.readDrying(
        entry
      );

      if (token !== this.loadToken) {
        return;
      }

      // Showing the stored rating means the scale reflects what you gave when you step back, and it
      // is what keeps a re-press of the same value from rewriting the document.
      this.savedReview = review;
      this.isSuppressed = suppression !== null;
      this.rating = review?.rating ?? null;

      this.savedNote = note;
      this.comment = note?.comment ?? "";
      this.chainSuspect = note?.chainSuspect ?? false;

      // Drop the previous drying's graph before swapping the chain in, so a stale canvas is never
      // shown against the new header. The legend's chain watcher then reports a fresh selection,
      // which is what triggers computeDryingGraphData.
      this.extractedGraphData = null;
      this.graphVisibleRange = null;
      this.iresDevice = device;
      this.programChain = chain;
    } catch {
      if (token === this.loadToken) {
        this.loadError = true;
        this.iresDevice = null;
        this.programChain = null;
        this.extractedGraphData = null;
      }
    } finally {
      if (token === this.loadToken) {
        this.isLoading = false;
        // The graph block only exists once loading ends, so it can only be measured after this.
        this.$nextTick(() => this.updateGraphViewportHeight());
      }
    }
  }

  /**
   * Reads everything a drying needs, reusing an in-flight or finished read of the same drying.
   *
   * The chain document is the expensive one: it carries every run with its samples inline, so on a long
   * drying it dwarfs the other four reads put together. Keeping the promise (not the result) means a
   * reviewer who presses Weiter while the prefetch is still running joins it instead of starting a
   * second copy of the same reads.
   *
   * One slot, not a cache: it holds the drying being looked at, then the one after it. Stepping back
   * simply misses and reads normally. The device read is allowed to come from the ComBox store, as
   * `DryingHistory` does, since a pass looks at many dryings of the same device and its configuration
   * cannot change retroactively.
   */
  private readDrying(entry: RenoReviewQueueEntry): Promise<DryingData> {
    const key = `${entry.serialNo}__${entry.chainId}__${entry.modelVersion}`;

    if (this.dryingRead?.key === key) {
      return this.dryingRead.data;
    }

    const data = Promise.all([
      getIresDevice(entry.serialNo, true),
      getProgramChain(entry.serialNo, entry.chainId),
      loadReview(this.reviewerKey, entry.serialNo, entry.chainId, entry.modelVersion),
      loadSuppression(this.reviewerKey, entry.serialNo, entry.chainId),
      // Best effort, unlike the four above. A note is an annotation: it must never be able to take the
      // drying down with it, and it is the one read here whose grant may not be deployed yet — which is
      // exactly what happened, turning a refused note read into "Die Trocknung konnte nicht geladen
      // werden" with no graph, no rating and no client name.
      loadNote(this.reviewerKey, entry.serialNo, entry.chainId).catch(() => null),
    ]).then(([device, chain, review, suppression, note]) => ({
      device,
      chain,
      review,
      suppression,
      note,
    }));

    // A prefetch nobody claims must not surface as an unhandled rejection. This derived promise absorbs
    // it; the stored one still rejects for whoever awaits it, which is what sets `loadError`.
    data.catch(() => undefined);

    this.dryingRead = { key, data };

    return data;
  }

  /**
   * Starts reading the next drying while the reviewer is still looking at this one.
   *
   * Called once the lookahead knows where Weiter goes, which is also what proves there *is* a next
   * drying. Costs one wasted set of reads when the reviewer rates and the queue then hides the drying,
   * and saves the reviewer the full round trip on every ordinary step.
   */
  private prefetchNextDrying() {
    const entry =
      this.isOnStepperRoute && this.nextStep
        ? currentEntry(this.nextStep.cursor)
        : null;

    if (entry) {
      this.readDrying(entry);
    }
  }

  /**
   * Resolves a client id to its display name, once per client.
   *
   * Cross-client reads are allowed here because admin-portal users hold
   * VIEW_HY_DRY_DEVICE_DETAILS_OF_ALL_CLIENTS. A failure is swallowed and the field keeps its
   * placeholder: a client id on screen is noise, not a fallback.
   */
  private async resolveClientName(clientId: string | null) {
    if (!clientId || this.clientNamesById[clientId] !== undefined) {
      return;
    }

    try {
      const name = getClientName(await getClient(clientId));

      if (name) {
        // Vue.set: a new key on a reactive object is not tracked by plain assignment in Vue 2.
        Vue.set(this.clientNamesById, clientId, name);
      }
    } catch {
      // Leave the id showing rather than blanking the field.
    }
  }

  /**
   * Builds the graph data for the drying on screen, using the legend's current sensor selection.
   *
   * Weather and power are deliberately off: they need per-client capability context and coordinate
   * lookups that would slow every step, and neither informs whether a RENO prediction was right.
   * `statusHistory` is empty because it is only ever populated for Panels, and RENO is ComBox-only.
   */
  private async computeDryingGraphData() {
    const device = this.iresDevice;
    const chain = this.programChain;

    if (!device || !chain || chain.id !== this.targetSensors.chainId) {
      return;
    }

    /*
    Weather is deliberately off here, while the power line is on.

    The consumption line comes out of the run samples the graph already has, so it is one flag. Weather
    needs the drying's project, that project's damage-location city, a geocoding lookup and then a
    weather request — per drying, and cross-client. Most reviewed chains carry no project at all, so the
    icon would mostly resolve to "not available".
    */
    const extras = graphPanel.resolveDryingGraphExtras({
      showPowerLine: this.showPowerLine,
      isAllowedToViewPowerInformation: viewPowerInformationAccess(
        getReactiveActiveUser()
      ),
      isAllowedToViewWeatherInformation: false,
      latitude: null,
      longitude: null,
      weatherInformationDetailsLevel: WeatherInformationDetailsLevel.NONE,
    });

    const data = await extractDryingProgressGraphData(
      this,
      device,
      chain.runs,
      this.targetSensors.selectedCombinations,
      graphPanel.buildDryingGraphOptions({
        isShowingActiveProgramChain: this.isDryingActive,
        lastRefreshDate: new Date(),
        latestDisplayedProgramChainId: "",
        programChainId: chain.id,
        showingOverviewProgramChain: false,
        targetSensors: this.targetSensors,
        isAllowedToViewRunStartsCausedByMaxRunDuration: true,
      }),
      [],
      extras.showPowerLine,
      extras.latitude,
      extras.longitude,
      extras.weatherInformationDetailsLevel,
      false,
      this.isDryingActive
    );

    // The legend may have moved on to the next drying while this was running.
    if (this.programChain?.id === chain.id) {
      this.extractedGraphData = data;
    }
  }

  /**
   * Persists the pending rating, if there is one worth writing.
   *
   * The ticket puts the write at the moment the reviewer steps onward, so this runs before every
   * forward step. Returns false when the write failed, which stops the step: silently advancing
   * past a lost rating is the one outcome a review pass cannot tolerate.
   *
   * On success the written verdict becomes `savedReview`, for the same reason the note write records
   * what it wrote: a rating given on the **last** drying of a pass does not move anywhere, so without
   * this the next Weiter would write it again and `holdsAppUpdate` would hold the update banner back
   * for as long as the reviewer stayed there.
   */
  private async persistPendingRating(): Promise<boolean> {
    const entry = currentEntry(this.cursor);

    if (!entry || this.rating === null || this.rating === this.savedReview?.rating) {
      return true;
    }

    this.isSavingRating = true;

    try {
      this.savedReview = await saveReview(
        this.reviewerKey,
        entry,
        this.rating,
        getReactiveActiveUser()?.nickname ?? null
      );

      return true;
    } catch {
      SnackbarModule.showError({ key: "admin.renoReview.rating.saveFailed" });

      return false;
    } finally {
      this.isSavingRating = false;
    }
  }

  /**
   * Persists the comment and the chain report, if either differs from what is stored.
   *
   * Blocks the step on failure, like a rating: a typed comment is deliberate work, and losing it
   * silently while moving the reviewer on is the same failure in a different field.
   *
   * On success the document that was written becomes `savedNote`, so `hasPendingNote` goes false. That
   * matters because the chain-report toggle no longer moves the reviewer on: without it, every
   * subsequent Weiter would write the same note over again.
   */
  private async persistPendingNote(): Promise<boolean> {
    const entry = currentEntry(this.cursor);

    if (!entry || !this.hasPendingNote) {
      return true;
    }

    this.isSavingRating = true;

    try {
      // Taken from the repository rather than rebuilt here, so "what is stored" is what actually
      // landed: `saveNote` trims the comment, maps an empty one to null and truncates at the cap.
      this.savedNote = await saveNote(this.reviewerKey, entry, {
        comment: this.comment,
        chainSuspect: this.chainSuspect,
      });

      return true;
    } catch {
      SnackbarModule.showError({ key: "admin.renoReview.note.saveFailed" });

      return false;
    } finally {
      this.isSavingRating = false;
    }
  }

  /**
   * Rating pressed: record it and move on.
   *
   * The button IS the step, which is why the write happens here rather than on Weiter. Pressing the
   * value already stored writes nothing and simply advances.
   */
  async onRate(value: number) {
    await this.runAction("rate", () => this.rate(value));
  }

  private async rate(value: number) {
    this.rating = value;
    this.pendingRating = value;

    try {
      if (!(await this.persistPendingRating())) {
        // The filled step means "this is what is stored", so a failed write must not leave the
        // pressed one filled: the reviewer would read it as saved and step on.
        this.rating = this.savedReview?.rating ?? null;

        return;
      }

      if (!(await this.persistPendingNote())) {
        return;
      }

      await this.stepForward();
    } finally {
      this.pendingRating = null;
    }
  }

  /**
   * Marks or unmarks the program chain as broken, and stays on the drying.
   *
   * A **toggle, not a step.** Reporting the chain says nothing about the prediction — it is a complaint
   * about the measured data — so it is independent of the verdict: the reviewer can report the chain and
   * still score the drying, in either order, and can take the report back. While this advanced, neither
   * was possible and a report could never be withdrawn from the UI at all.
   *
   * Written immediately rather than on the next step, so the pressed state always means "this is
   * stored" and leaving the screen cannot lose it. The comment goes with it, since both live in one
   * document.
   */
  async onToggleChainSuspect() {
    await this.runAction("toggleChainSuspect", async () => {
      // Guarded like the hide toggle: with no drying under the cursor `persistPendingNote` has nothing
      // to write and reports success, which would leave the button filled over nothing stored.
      if (!currentEntry(this.cursor)) {
        return;
      }

      const previous = this.chainSuspect;

      this.chainSuspect = !previous;

      if (!(await this.persistPendingNote())) {
        // Same reason as a failed rating: a filled button must not claim a mark that was refused.
        this.chainSuspect = previous;
      }
    });
  }

  async onNext() {
    await this.runAction("next", async () => {
      if (!(await this.persistPendingRating()) || !(await this.persistPendingNote())) {
        return;
      }

      await this.stepForward();
    });
  }

  /**
   * Hides the drying from this reviewer's queue, or puts it back, and stays on the drying.
   *
   * A **toggle, not a step**, for the same reasons as the chain report: hiding says nothing about
   * prediction quality, so it is independent of the verdict, and a mark that cannot be taken back on
   * the screen that set it is a trap. Un-hiding is deleting the suppression, exactly as the
   * AUSGEBLENDETE TROCKNUNGEN screen does it — that screen remains the way back to a drying hidden in
   * an earlier pass, together with the queue's "Ausgeblendet" filter.
   *
   * A suppression is its own store rather than a rating, because it must survive every future RENO
   * release. Written immediately so the pressed state always means "this is stored"; the drying stays
   * in the pass until it is left, which is what makes the second press possible.
   */
  async onToggleSuppress() {
    await this.runAction("toggleSuppress", async () => {
      const entry = currentEntry(this.cursor);

      if (!entry) {
        return;
      }

      const wasSuppressed = this.isSuppressed;

      try {
        if (wasSuppressed) {
          await removeSuppression(this.reviewerKey, entry.serialNo, entry.chainId);
        } else {
          await saveSuppression(this.reviewerKey, entry);
        }

        this.isSuppressed = !wasSuppressed;
      } catch {
        // Its own message per direction: "could not be hidden" and "could not be shown again" are
        // different failures, and the second one reuses the un-hide screen's wording.
        SnackbarModule.showError({
          key: wasSuppressed
            ? "admin.renoReview.hidden.restoreFailed"
            : "admin.renoReview.rating.suppressFailed",
        });
      }
    });
  }

  /**
   * Records the current drying as seen, then advances.
   *
   * Seen-ness is written on the way out rather than on arrival, so merely loading a drying and
   * navigating away with the browser back button does not mark it. A failure here does NOT block the
   * step: unlike a rating, a missing seen record costs the reviewer nothing but a repeat sighting.
   */
  private async stepForward() {
    const entry = currentEntry(this.cursor);

    // The shared IRES admin login leaves no seen records either (#2610071); the rules refuse them.
    if (entry && this.mayEdit) {
      // NOT awaited: a failure already does not block the step, so waiting for the round trip only
      // delayed every Weiter by one write. The SDK keeps retrying it in the background.
      saveSeen(this.reviewerKey, entry).catch(() => {
        // Best effort; see above.
      });
    }

    // Already resolved by the lookahead in the common case, so this reuses that walk rather than
    // repeating it. `applyStep` on an already-settled step performs no further loads.
    const step = this.nextStep ?? next(this.cursor);

    // The direction is what lets a boundary reached by pressing disable Weiter and explain itself.
    if (await this.applyStep(step, "next")) {
      await this.loadCurrentDrying();
    }
  }

  /**
   * Steps back.
   *
   * Backwards does not save a **rating**: stepping back is how you go and look, not how you rate. It
   * does flush a comment or a chain report, because those are not judgements of the drying you are
   * leaving and dropping typed text on a back press is data loss either way.
   */
  async onPrevious() {
    await this.runAction("previous", async () => {
      if (!(await this.persistPendingNote())) {
        return;
      }

      // Reuses the lookahead's walk, exactly as stepping forward does.
      const step = this.previousStep ?? previous(this.cursor);

      if (await this.applyStep(step, "previous")) {
        await this.loadCurrentDrying();
      }
    });
  }

  onBackToFilters() {
    // Filters travel back to the filter screen; the resume position does not. Left in, it would be
    // read as part of the next pass's query and open that pass halfway through.
    const query = { ...this.$route.query };
    delete query.atSerial;
    delete query.atChainId;

    router.push({ name: ComponentName.RENO_REVIEW_QUEUE, query });
  }

  // Access (portal client + RATE_DRYINGS) is enforced in router.beforeEach, which can await the
  // asynchronous role-settings load. A capability check here would run before that arrives and
  // bounce the reviewer out of the screen on every page refresh.
  /**
   * The graph's options with the screen width's tick font and number of dates, as on the device
   * detail (`graphSizeProfileFor`): on an iPad the desktop's dates ran into each other (2026-11-03).
   * The axis titles stay on the axes, since this screen has no titles line above the plot.
   */
  get graphOptions() {
    const options = this.dryingProgressChartData?.options;
    const profile = { ...graphSizeProfileFor(this.$vuetify.breakpoint), titlesAbovePlot: false };
    return options ? applyCompactGraphOptions(options, profile) : options;
  }

  /** Below 600 px: the hint instead of the stepper (PhoneEditingHint, Taimoor 2026-11-03). */
  get isPhone(): boolean {
    return this.$vuetify.breakpoint.xsOnly;
  }

  mounted() {
    ToolbarContentModule.setI18nResource("admin.renoReview.title");
    // Not on a phone, so nothing is read or marked seen there; a screen turned wider starts it then.
    if (!this.isPhone) {
      this.startReview();
    }
  }

  @Watch("isPhone")
  onIsPhoneChanged(isPhone: boolean) {
    if (!isPhone && !this.hasStarted) {
      this.startReview();
    }
  }

  /** Loads the first devices and opens the first drying: what `mounted` did before the phone check. */
  async startReview() {
    this.hasStarted = true;
    window.addEventListener("resize", this.onWindowResize);

    try {
      const startAtSerial = (this.$route.query.startAtSerial as string) || null;
      // Where a previous visit left off, written by `syncResumeLocation`. The track is still loaded
      // from `startAtSerial`, so resuming does not cut off the part of the pass already walked.
      const resumeSerial = (this.$route.query.atSerial as string) || "";
      const resumeChainId = (this.$route.query.atChainId as string) || "";
      const devices = await loadReviewDevices(
        this.filters,
        DEVICE_PAGE_SIZE,
        startAtSerial
      );

      if (devices.length === 0) {
        this.isEmptyQueue = true;
        this.isLoading = false;
        return;
      }

      if (await this.applyStep(startCursor(devices, resumeSerial))) {
        if (resumeChainId) {
          this.cursor = landOnChain(this.cursor, resumeChainId);
        }

        await this.loadCurrentDrying();
      } else {
        // Devices matched but every one of them filtered down to no chains.
        this.isEmptyQueue = true;
        this.isLoading = false;
      }
    } catch {
      this.loadError = true;
      this.isLoading = false;
    }
  }

  /**
   * True while the reviewer has work a reload would lose.
   *
   * A rating is only written when they step onward, so a selection that differs from the stored
   * review is unsaved work. The condition mirrors {@link persistPendingRating} on purpose: anything
   * that would be written by the next Weiter is worth holding the update for. An already-rated
   * drying does not hold, or a whole review pass would suppress the banner from end to end.
   */
  get holdsAppUpdate(): boolean {
    // Any action in flight, not just a write: reloading the app between the write and the step is how a
    // rating lands with the reviewer looking at a different drying.
    if (this.isSavingRating || this.pendingAction !== null) {
      return true;
    }

    // A typed comment is unsaved work too, and a reload would drop it. The two marks are not: they are
    // written the moment they are pressed, so `hasPendingNote` can only be the comment here.
    return (
      (this.rating !== null && this.rating !== this.savedReview?.rating) ||
      this.hasPendingNote
    );
  }

  /** The IRES-capability toggle (long press on the app bar's back arrow), watched below. */
  get ignoreIresAdminOrIresClientCheck(): boolean {
    return ClientModule.ignoreIresAdminOrIresClientCheck;
  }

  /**
   * Re-reads the drying when the IRES capabilities are toggled.
   *
   * The outlier filter runs while **decoding** samples, and the decode overwrites the raw payload, so the
   * chain in hand cannot be redrawn into the other view: it has to be read again. Dropping the cached
   * read is what forces that, since `readDrying` would otherwise hand back the same decoded chain.
   */
  @Watch("ignoreIresAdminOrIresClientCheck")
  async onIgnoreIresAdminOrIresClientCheckChanged() {
    this.dryingRead = undefined;
    await this.loadCurrentDrying();
  }

  @Watch("holdsAppUpdate")
  onHoldsAppUpdateChanged(holdsAppUpdate: boolean) {
    setUpdateHold("reno-review-stepper", holdsAppUpdate);
  }

  beforeDestroy() {
    setUpdateHold("reno-review-stepper", false);
    window.removeEventListener("resize", this.onWindowResize);
  }
}
